import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import type { DialogProps } from '../../components/dialogs/DialogProvider';
import { colours, radius } from '../../theme';

/**
 * Shows a temporary password once, with a Copy button. With `another`, a button beside it sets a
 * different one instead (the one shown stops working).
 */
export function TemporaryPasswordDialog({ name, password: first, another, close }: { name: string; password: string; another?(): Promise<string> } & DialogProps) {
  const [password, setPassword] = useState(first);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  return (
    <Dialog open onClose={() => close()} maxWidth="xs" fullWidth aria-labelledby="temp-title">
      <DialogTitle id="temp-title">Temporary password for {name}</DialogTitle>
      <DialogContent>
        <Typography color="textSecondary" sx={{ mb: 2 }}>
          Give this to them in person or by phone. They will choose their own when they sign in. It won't be shown again.
        </Typography>
        <Box sx={{ position: 'relative', p: 2, px: 6, bgcolor: colours.paleGrey, borderRadius: `${radius.panel}px`, textAlign: 'center' }}>
          <Box className="num" component="span" sx={{ fontSize: 22, fontWeight: 500, letterSpacing: '0.04em', userSelect: 'all', opacity: busy ? 0.4 : 1 }}>{password}</Box>
          {another && (
            <Tooltip title="Use another password">
              <IconButton
                aria-label="Use another password"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    setPassword(await another());
                    setCopied(false);
                  } finally {
                    setBusy(false);
                  }
                }}
                sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
              >
                <RefreshCw size={20} strokeWidth={1.75} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Alert severity="info" icon={false} sx={{ mt: 2, color: 'text.secondary' }}>
          They have been signed out everywhere.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={async () => {
            await navigator.clipboard?.writeText(password).catch(() => undefined);
            setCopied(true);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button variant="contained" onClick={() => close()}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
